import { useState, type FormEvent } from 'react';
import { Flag, AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';

/**
 * Small modal for raising a flag on a person. Free text only, no
 * categories. Flags never block kit issue or check-in; they're a
 * reminder to chase the inductee verbally.
 */
export function RaiseFlagModal({
  open,
  personName,
  onClose,
  onSubmit,
}: {
  open: boolean;
  personName: string;
  onClose: () => void;
  onSubmit: (input: { reason: string }) => void | Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!reason.trim()) {
      setError('Please describe the flag');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit({ reason: reason.trim() });
      setReason('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to raise flag');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="raise-flag-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-fg/40 p-4 backdrop-blur-sm aurora-fade-in"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-lg"
      >
        <header className="flex items-center gap-3 border-b border-divider/10 bg-warning-soft/15 px-5 py-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-token-sm bg-warning/15 text-warning">
            <Flag className="h-5 w-5" />
          </div>
          <div>
            <h2 id="raise-flag-title" className="text-base font-semibold tracking-tight text-fg">
              Raise flag
            </h2>
            <p className="text-xs text-fg-muted">
              for <strong>{personName}</strong>
            </p>
          </div>
        </header>

        <div className="space-y-4 p-5 sm:p-6">
          <p className="text-xs text-fg-muted">
            A flag is a reminder — it shows up on every desk but does not
            stop check-in or kit issue. Anyone can clear it once resolved.
          </p>

          <label className="block text-sm">
            <span className="font-medium text-fg">Reason</span>
            <textarea
              required
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. DBS paperwork still missing, remind them to drop it at HR before leaving"
              className="mt-1.5 block w-full rounded-token-sm border border-line-strong/20 bg-surface px-3 py-2 text-sm text-fg shadow-token-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-ring/30"
            />
          </label>

          {error ? (
            <div className="flex items-start gap-2 rounded-token-sm bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-divider/10 bg-surface-2 px-5 py-3">
          <Button variant="secondary" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="warning"
            size="md"
            iconLeft={<Flag className="h-4 w-4" />}
            loading={submitting}
          >
            Raise flag
          </Button>
        </footer>
      </form>
    </div>
  );
}
